import { Injectable } from '@angular/core';
import { DependencyActionEnum, getPermissionEnumKey, LorenzFormControl, LorenzFormElement, LorenzFormGroup, LorenzFormList, ProcessStatusEnum } from '@lib/forms';
import { debounceTime, delay } from 'rxjs';
import { AbstractControl } from '@angular/forms';
import { formatSystemDate } from '@lib/shared';

@Injectable({
	providedIn: 'root'
})
export class LorenzFormService {
	// Types a control for the LorenzFormWorld if possible.
	typifyLorenzFormElement(control: any): LorenzFormGroup | LorenzFormList | LorenzFormControl | null {
		if (control instanceof LorenzFormList) {
			return control as LorenzFormList;
		}
		if (control instanceof LorenzFormGroup) {
			return control as LorenzFormGroup;
		}
		if (control instanceof LorenzFormControl) {
			return control as LorenzFormControl;
		}
		return null;
	}

	// Returns a real LorenzFormGroup or null.
	isLorenzFormGroup(control: any): LorenzFormGroup | null {
		if (control instanceof LorenzFormGroup) {
			return control as LorenzFormGroup;
		}
		return null;
	}

	// Returns a real LorenzFormList or null.
	isLorenzFormList(control: any): LorenzFormList | null {
		if (control instanceof LorenzFormList) {
			return control as LorenzFormList;
		}
		return null;
	}

	// Returns a real LorenzFormControl or null.
	isLorenzFormControl(control: any): LorenzFormControl | null {
		if (control instanceof LorenzFormControl) {
			return control as LorenzFormControl;
		}
		return null;
	}

	// Collects all child-groups of a group or a list sorted in an array.
	convertChildGroupsToArray(lorenzFormCollection: LorenzFormGroup | LorenzFormList | null): LorenzFormGroup[] {
		if (!lorenzFormCollection?.controls) {
			return [] as LorenzFormGroup[];
		}

		// prettier-ignore
		const helper1 = Object.values(lorenzFormCollection.controls)
			.filter((control) => control instanceof LorenzFormGroup) as LorenzFormGroup[];

		const helper2 = helper1.sort((a, b) => {
			const orderA = a.payload?.order ?? '';
			const orderB = b.payload?.order ?? '';
			return orderA < orderB ? -1 : orderA > orderB ? 1 : 0;
		});

		return helper2;
	}

	// Collects all child-lists of a group sorted in an array.
	convertChildListsToArray(lorenzFormGroup: LorenzFormGroup | null): LorenzFormList[] {
		if (!lorenzFormGroup?.controls) {
			return [] as LorenzFormList[];
		}

		// prettier-ignore
		const helper1 = Object.values(lorenzFormGroup.controls)
			.filter((control) => control instanceof LorenzFormList) as LorenzFormList[];

		const helper2 = helper1.sort((a, b) => {
			const orderA = a.payload?.order ?? '';
			const orderB = b.payload?.order ?? '';
			return orderA < orderB ? -1 : orderA > orderB ? 1 : 0;
		});

		return helper2;
	}

	// Returns all children of a group or a list in a sorted array.
	getChildren(lorenzFormCollection: LorenzFormGroup | LorenzFormList): (LorenzFormGroup | LorenzFormList | LorenzFormControl)[] {
		if (!lorenzFormCollection?.controls) {
			return [] as LorenzFormControl[];
		}

		// prettier-ignore
		const helper1 = Object.values(lorenzFormCollection.controls)
			.filter(control =>
				control instanceof LorenzFormGroup ||
				control instanceof LorenzFormList ||
				control instanceof LorenzFormControl
			) as (LorenzFormGroup | LorenzFormList | LorenzFormControl)[];

		const helper2 = helper1.sort((a, b) => {
			const orderA = a.payload?.order ?? '';
			const orderB = b.payload?.order ?? '';
			return orderA < orderB ? -1 : orderA > orderB ? 1 : 0;
		});

		return helper2;
	}

	// Returns a child-group or NULL that was found based on the name of a group.
	getLorenzFormGroup(lorenzFormGroup: LorenzFormGroup, lorenzFormGroupName: string): LorenzFormGroup | null {
		if (!lorenzFormGroup || !lorenzFormGroupName) {
			return null;
		}
		const helper1 = (lorenzFormGroup.get(lorenzFormGroupName) as LorenzFormGroup) ?? null;
		return helper1;
	}

	// Returns a list or NULL that was found based on the name of a list.
	getLorenzFormList(lorenzFormGroup: LorenzFormGroup, lorenzFormListName: string): LorenzFormList | null {
		if (!lorenzFormGroup || !lorenzFormListName) {
			return null;
		}
		const helper1 = (lorenzFormGroup.get(lorenzFormListName) as LorenzFormList) ?? null;
		return helper1;
	}

	// Returns a control or NULL that was found based on the name of a control.
	getLorenzFormControl<T = any>(lorenzFormGroup: LorenzFormGroup, lorenzFormControlName: string): LorenzFormControl | null {
		if (!lorenzFormGroup || !lorenzFormControlName) {
			return null;
		}

		const controlPath = lorenzFormControlName.split('.');
		let control: any = lorenzFormGroup;

		for (const segment of controlPath) {
			if (control && typeof control.get === 'function') {
				control = control.get(segment);
			} else if (control && control.controls && control.controls[segment]) {
				control = control.controls[segment];
			} else {
				return null;
			}
		}

		return control as LorenzFormControl<T>;
	}

	// Returns all visible child-groups of a group in an array.
	getVisibleChildGroups(lorenzFormCollection: LorenzFormGroup | LorenzFormList): LorenzFormGroup[] {
		// prettier-ignore
		const helper1 = this.convertChildGroupsToArray(lorenzFormCollection);
		const helper2 = helper1.filter((lorenzFormCollection) => lorenzFormCollection?.isVisible());

		return helper2;
	}

	// Returns all visible child-lists of a group in an array.
	getVisibleChildLists(lorenzFormGroup: LorenzFormGroup): LorenzFormList[] {
		// prettier-ignore
		const helper1 = this.convertChildListsToArray(lorenzFormGroup);
		const helper2 = helper1.filter((lorenzFormArray) => lorenzFormArray?.isVisible());

		return helper2;
	}

	// Gibt die LorenzFormGroup rekursiv aus.
	printFormStructure(control: AbstractControl, indent: string = '') {
		const indentSpace = '   ';

		if (control instanceof LorenzFormGroup) {
			console.log(indent + 'GROUP "' + control.getKey() + '" =>', control.isValidationEnabled(), getPermissionEnumKey(control.getPermission()));
			Object.keys(control.controls).forEach((key) => {
				// console.log(indent + indentSpace + key);
				this.printFormStructure(control.controls[key], indent + indentSpace);
			});
		} else if (control instanceof LorenzFormList) {
			console.log(indent + 'LIST "' + control.getKey() + '" =>', getPermissionEnumKey(control.getPermission()));
			control.controls.forEach((ctrl, index) => {
				console.log(indent + indentSpace + index);
				this.printFormStructure(ctrl, indent + indentSpace);
			});
		} else if (control instanceof LorenzFormControl) {
			console.log(indent + control.getKey() + ' =>', getPermissionEnumKey(control.getPermission()));
		}
	}

	// Cleans up a FormGroup.
	purgeFormGroup(element: LorenzFormElement): void {}

	// Delivers the salutation gender text by id.
	salutationText(idx: string): string {
		const salutations: Record<string, string> = {
			'1': 'Frau',
			'2': 'Herr'
		};
		return salutations[idx] ?? 'Keine Angaben';
	}

	// Delivers the full salutation gender text by id.
	salutationFullname(idx: string, fullname: string): string {
		fullname = fullname ?? '';
		const salutations: Record<string, string> = {
			'1': 'Sehr geehrte Frau ' + fullname,
			'2': 'Sehr geehrter Herr ' + fullname
		};
		return (salutations[idx] ?? 'Guten Tag ' + fullname).trim();
	}

	updateLastUpdate(lorenzFormGroup: LorenzFormGroup): void {
		const lorenzFormControl = lorenzFormGroup.get('_lorenz.last_update');
		if (lorenzFormControl) {
			lorenzFormControl.patchValue(formatSystemDate(), { emitEvent: false });
			lorenzFormControl.updateValueAndValidity({ onlySelf: true });
		}
	}

	// Patch values (if provided).
	setValues(lorenzFormGroup: LorenzFormGroup, patchValues: any) {
		if (patchValues) {
			try {
				console.log('lorenzFormService.setValues', lorenzFormGroup, patchValues);
				lorenzFormGroup.forcedValuePatching = true;
				lorenzFormGroup.patchValue(patchValues, { emitEvent: false });
				lorenzFormGroup.forcedValuePatching = false;
			} catch (e) {
				console.error(e);
			}
		}
	}

	// Set LorenzFormGroup STATUS.
	setStatus(lorenzFormGroup: LorenzFormGroup | null, status: ProcessStatusEnum) {
		if (lorenzFormGroup) {
			const lorenzFormGroupRoute = lorenzFormGroup.getRoot();
			const lorenzFormControlStatus = lorenzFormGroupRoute?.get('_lorenz.status');
			if (lorenzFormControlStatus) {
				lorenzFormControlStatus.patchValue(status, { emitEvent: false });
			}
		}
	}

	// Initializes the validation for all elements.
	initValidations(lorenzFormElement: LorenzFormElement) {
		const isLocked = lorenzFormElement.isLocked();
		const isVisible = lorenzFormElement.isVisible();
		const hasValidation = isVisible && !isLocked;
		// console.log('initValidations', lorenzFormElement.getFullKey(), isVisible, isLocked, hasValidation);
		lorenzFormElement.setValidation(hasValidation);

		if (lorenzFormElement instanceof LorenzFormGroup) {
			Object.keys(lorenzFormElement.controls).forEach((key) => {
				this.initValidations(lorenzFormElement.controls[key] as LorenzFormElement);
			});
		} else if (lorenzFormElement instanceof LorenzFormList) {
			lorenzFormElement.controls.forEach((subControl, index) => {
				this.initValidations(subControl as LorenzFormElement);
			});
		}
	}

	// Initializes the valueBuffer for all elements.
	initValueBuffer(lorenzFormElement: LorenzFormElement) {
		// if (lorenzFormElement.getFullKey() === 'beschaeftigungen_abgeschlossen.liste') {
		// 	console.log('BUMM');
		// }

		const isDisabled = lorenzFormElement.isDisabled() && lorenzFormElement.getKey() !== 'checked';

		// Wenn das element deaktiviert ist, wird der Wert im Buffer gespeichert.
		if (isDisabled) {
			lorenzFormElement.backupAndRemoveValue();
		}

		lorenzFormElement._valueBufferInitialized = true; // TODO

		if (lorenzFormElement instanceof LorenzFormGroup) {
			Object.keys(lorenzFormElement.controls).forEach((key) => {
				this.initValueBuffer(lorenzFormElement.controls[key] as LorenzFormElement);
			});
		} else if (lorenzFormElement instanceof LorenzFormList) {
			lorenzFormElement.controls.forEach((subControl, index) => {
				this.initValueBuffer(subControl as LorenzFormElement);
			});
		}
	}

	// Creates all dependencies in the complete form.
	applyDependencies(rootGroup: LorenzFormGroup, lorenzFormElement: LorenzFormElement): void {
		if (!rootGroup || !lorenzFormElement) {
			return;
		}

		// The 'valueChanges' handler.
		if (lorenzFormElement.payload?.valueChanges) {
			const customFunction = lorenzFormElement.payload?.valueChanges;
			if (typeof customFunction === 'function') {
				customFunction(lorenzFormElement.value, rootGroup, lorenzFormElement); // First time activation of the dependency.
				const runImmediately = lorenzFormElement.payload?.valueChangesImmediate === true;
				if (runImmediately) {
					lorenzFormElement.valueChanges.subscribe((value) => {
						customFunction(value, rootGroup, lorenzFormElement);
					});
				} else {
					// prettier-ignore
					lorenzFormElement.valueChanges
						.pipe(delay(456))
						.subscribe((value) => {
							customFunction(value, rootGroup, lorenzFormElement);
						});
				}
			}
		}

		// The 'dependencies' handler.
		if (lorenzFormElement.payload?.dependencies) {
			lorenzFormElement.payload.dependencies.forEach((dependency: any) => {
				let controlName = dependency.controlName;

				// If "controlName" begins with ".", the control to be observed is searched for in its own FormGroup.
				// If not, the control to be observed is searched for in its complete form.
				let searchRoot;
				if (controlName.startsWith('.')) {
					searchRoot = lorenzFormElement.parent;
					controlName = controlName.slice(1);
				} else {
					searchRoot = rootGroup;
				}

				if (!searchRoot) {
					return;
				}

				const dependencyControl = searchRoot.get(controlName) as LorenzFormElement;
				if (dependencyControl) {
					if (dependency.debug) {
						console.log('DEPENDENCY SETUP for "' + lorenzFormElement.getFullKey() + '"');
					}

					this.setControlActivation(dependencyControl.value, dependency, lorenzFormElement, dependencyControl); // First time activation of the dependency.

					const debounceMs = typeof dependency.debounceMs === 'number' ? Math.max(0, dependency.debounceMs) : 123;
					// prettier-ignore
					dependencyControl.valueChanges
						.pipe(debounceTime(debounceMs))
						.subscribe((value: any) => {
							this.setControlActivation(value, dependency, lorenzFormElement, dependencyControl);
						});
				}
			});
		}

		if (lorenzFormElement instanceof LorenzFormGroup) {
			Object.keys(lorenzFormElement.controls).forEach((key) => {
				this.applyDependencies(rootGroup, lorenzFormElement.controls[key] as LorenzFormElement);
			});
		} else if (lorenzFormElement instanceof LorenzFormList) {
			lorenzFormElement.controls.forEach((subControl, index) => {
				this.applyDependencies(rootGroup, subControl as LorenzFormElement);
			});
		}
	}

	// Monitors the condition for a dependency and triggers the action if necessary.
	private setControlActivation(value: any, dependency: any, element: LorenzFormElement, dependencyControl: LorenzFormElement) {
		let conditionFits;
		//
		// if (element.getFullKey() === 'beschaeftigungen_abgeschlossen.liste') {
		// 	console.log('BESCHÄFTIGUNGEN ABGESCHLOSSEN', element.getFullKey(), value, dependency);
		// }
		if (dependency.expectedValue) {
			// prettier-ignore
			conditionFits = typeof dependency.expectedValue === 'function'
				? !!dependency.expectedValue(value)
				: value === dependency.expectedValue;
		} else {
			conditionFits = true;
		}

		if (dependency.debug) {
			console.log('DEPENDENCY FIRED "' + element.getFullKey() + '" with value "' + value + '" =>', conditionFits);
		}

		let validateForm = false;

		if (conditionFits) {
			switch (dependency.action) {
				case DependencyActionEnum.CODE: // Sets the value of the control to the function result if the condition is met
					if (dependency.actionFunction && typeof dependency.actionFunction === 'function') {
						const result = dependency.actionFunction(value, element, dependencyControl);
						element.setValue(result);
					}
					break;
				case DependencyActionEnum.DISABLE: // Disables the control if the condition is met
					if (element.isEnabled()) {
						validateForm = true;
						element.disable();
					}
					break;
				case DependencyActionEnum.ENABLE: // Enables the control if the condition is met
					if (element.isDisabled()) {
						validateForm = true;
						element.enable();
					}
					break;
				case DependencyActionEnum.ENABLE_CODE: // Enables the control and executes a function if the condition is met
					if (element.isDisabled()) {
						validateForm = true;
						element.enable();
						if (dependency.actionFunction && typeof dependency.actionFunction === 'function') {
							dependency.actionFunction(value, element, dependencyControl);
						}
					}
					break;
				case DependencyActionEnum.LOCK: // Locks the control if the condition is met
					element.setLocked(true);
					break;
			}
		} else {
			switch (dependency.action) {
				case DependencyActionEnum.CODE:
					// DO NOTHING
					break;
				case DependencyActionEnum.DISABLE:
					if (element.isDisabled()) {
						validateForm = true;
						element.enable();
					}
					break;
				case DependencyActionEnum.ENABLE:
					if (element.isEnabled()) {
						validateForm = true;
						element.disable();
						element.reset();
					}
					break;
				case DependencyActionEnum.ENABLE_CODE:
					if (element.isEnabled()) {
						validateForm = true;
						element.disable();
						if (dependency.actionFunction && typeof dependency.actionFunction === 'function') {
							dependency.actionFunction(value, element);
						}
					}
					break;
				case DependencyActionEnum.LOCK: // Unlocks the control if the condition is met
					element.setLocked(false);
					break;
			}
		}

		if (validateForm) {
			element.getRoot().updateValueAndValidity();
		}
	}
}
